/**
 * Het gesprek in het midden, over alle kanalen heen (ontwerp 26):
 * - mail als kaart (afzender, aan, tijd, onderwerp, tekst);
 * - chat, WhatsApp en telefoon als ballonnen: klant links wit, wij
 *   rechts lichtblauw met "Nova, 08:31, gelezen";
 * - foto's in de ballon, spraak met afspeelknop en transcript;
 * - interne notitie als dunne gele regel, systeem klein en grijs.
 * Live: "typt..." van de klant en "Nova antwoordt..." zonder verversen.
 *
 * Kale links in platte tekst (ballon, transcript, mail zonder html) zijn
 * klikbaar via `metLinks`. Een klantmail staat in `MailWeergave` (sessie
 * 186): opgemaakt in het veilige vlak (`Mailvlak`, sessie 75) of als tekst
 * zonder markeringen, met het eerdere bericht ingeklapt; onze eigen mail loopt via
 * `veiligeHtml` en blijft een kaart in werkplekstijl. Eerdere
 * gesprekken uit dezelfde draad staan achter één inklapregel, gedempt.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { emailsApi } from "@/lib/api";

import {
  KANAAL_WOORD,
  v6Api,
  type V6Bericht,
  type V6Bijlage,
  type V6Foto,
  type V6Mail,
  type V6Zaak,
} from "../api";
import { Icoon } from "../Icoon";
import { useLive } from "../live";
import { Gespreksregel } from "./Gespreksregels";
import "./gespreksregels.css";
import { metLinks } from "./links";
import { Lightbox } from "./Lightbox";
import { MailWeergave, zonderMarkering } from "./MailWeergave";
import { dagKlok, duurWoord, klokje } from "./tijd";
import { veiligeHtml } from "./veiligeHtml";

/** Hoe lang na het laatste klantbericht "Nova antwoordt..." mag staan. */
const NOVA_BEZIG_MS = 60_000;

function initiaal(wie: string): string {
  return (wie.trim()[0] ?? "?").toUpperCase();
}

/** Platte tekst als alinea's; enkele regelovergangen blijven staan. */
function Alineas({ tekst, markdown = false }: { tekst: string; markdown?: boolean }) {
  const delen = tekst
    .split(/\n\s*\n/)
    .map((d) => d.trim())
    .filter(Boolean);
  return (
    <>
      {delen.map((deel, i) => (
        <p key={i} className="regels">
          {metLinks(deel, { markdown })}
        </p>
      ))}
    </>
  );
}

function Inhoud({ bericht }: { bericht: V6Bericht }) {
  // De pagina tikt elke 10 s (live-venster); ontleed de HTML niet elke keer.
  const schoon = useMemo(() => (bericht.html ? veiligeHtml(bericht.html) : null), [bericht.html]);
  if (bericht.mail) {
    return <MailWeergave emailId={bericht.id} mail={bericht.mail} />;
  }
  if (schoon !== null) {
    return <div dangerouslySetInnerHTML={{ __html: schoon }} />;
  }
  return <Alineas tekst={bericht.tekst} markdown={bericht.kant === "wij"} />;
}

/** Een foto uit de ballon of mail, als blob geladen; klik opent hem groot. */
export function Foto({ foto }: { foto: V6Foto }) {
  const [url, setUrl] = useState<string | null>(null);
  const [mislukt, setMislukt] = useState(false);
  const [groot, setGroot] = useState(false);

  // Op de primitieve velden: een ververste zaak geeft nieuwe objecten,
  // en dan hoeft de foto niet opnieuw geladen te worden.
  const { id, bron, email_id } = foto;
  useEffect(() => {
    let actief = true;
    let gemaakt: string | null = null;
    v6Api
      .laadFoto({ id, bron, email_id, naam: "", blik: null })
      .then((blob) => {
        if (!actief) return;
        gemaakt = URL.createObjectURL(blob);
        setUrl(gemaakt);
      })
      .catch(() => {
        if (actief) setMislukt(true);
      });
    return () => {
      actief = false;
      if (gemaakt) URL.revokeObjectURL(gemaakt);
    };
  }, [id, bron, email_id]);

  const beschrijving = foto.blik ?? foto.naam;
  const sluit = useCallback(() => setGroot(false), []);

  return (
    <>
      <button
        type="button"
        className="foto"
        title={beschrijving}
        disabled={!url}
        onClick={() => setGroot(true)}
      >
        {url ? (
          <img src={url} alt={beschrijving} />
        ) : (
          <span className="v6-leeg">{mislukt ? "Foto niet geladen" : ""}</span>
        )}
      </button>
      {groot && url && (
        <Lightbox url={url} beschrijving={beschrijving} onSluit={sluit} />
      )}
    </>
  );
}

/** Spraakbericht of voicemail: afspelen als er een opname is, duur, transcript. */
function Spraak({ bericht }: { bericht: V6Bericht }) {
  const [url, setUrl] = useState<string | null>(null);
  const [fout, setFout] = useState(false);
  const [bezig, setBezig] = useState(false);

  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  const speel = async () => {
    if (!bericht.audio) return;
    setBezig(true);
    setFout(false);
    try {
      const blob = await v6Api.laadAudio(bericht.audio);
      setUrl(URL.createObjectURL(blob));
    } catch {
      setFout(true);
    } finally {
      setBezig(false);
    }
  };

  return (
    <>
      <div className="spraak">
        {url ? (
          <audio src={url} controls autoPlay />
        ) : (
          <>
            {bericht.audio && (
              <button
                type="button"
                className="p"
                aria-label="Afspelen"
                disabled={bezig}
                onClick={() => void speel()}
              >
                <Icoon naam="play" klein />
              </button>
            )}
            <span className="golf" />
          </>
        )}
        <span className="duur">{duurWoord(bericht.duur_seconden)}</span>
      </div>
      {fout && <p className="v6-fout">Opname niet geladen</p>}
      {bericht.transcript && <div className="transcript">{metLinks(bericht.transcript)}</div>}
    </>
  );
}

function isSpraak(bericht: V6Bericht): boolean {
  return (
    bericht.bericht_soort === "spraak" ||
    bericht.bericht_soort === "voicemail" ||
    // Sessie 73 blok 4: een belgesprek met verslag en lege tekst toont het
    // verslag zoals een voicemail, nooit een lege ballon.
    (bericht.bericht_soort === "gesprek" && Boolean(bericht.transcript?.trim()))
  );
}

/**
 * Een bijlage van een mail: tik bewaart hem (sessie 75). Downloaden, niet
 * openen: een HTML- of SVG-bijlage van een klant mag nooit op onze eigen
 * oorsprong draaien, en iOS toont een blob in een nieuw tabblad vaak leeg.
 */
function Bijlage({ bijlage }: { bijlage: V6Bijlage }) {
  const [fout, setFout] = useState(false);
  if (!bijlage.email_id) {
    return (
      <span className="v6-chip">
        <Icoon naam="klip" klein />
        {bijlage.naam}
      </span>
    );
  }
  const emailId = bijlage.email_id;
  const bewaar = async () => {
    try {
      const ruw = await emailsApi.bijlageInhoud(emailId, bijlage.id);
      // Altijd als bestand, nooit als pagina: het type gaat eraf.
      const blob = new Blob([ruw], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = bijlage.naam || "bijlage";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setFout(false);
    } catch {
      setFout(true);
    }
  };
  return (
    <button type="button" className={`v6-chip bijlage${fout ? " fout" : ""}`} onClick={() => void bewaar()}>
      <Icoon naam="klip" klein />
      {bijlage.naam}
      {fout ? " (niet geladen)" : null}
    </button>
  );
}

/** Foto's, spraak en tekst: wat in een ballon en in een mailkaart staat. */
function Lijf({ bericht }: { bericht: V6Bericht }) {
  const spraak = isSpraak(bericht);
  const tekstIsTranscript =
    spraak && (!bericht.tekst.trim() || bericht.tekst.trim() === bericht.transcript?.trim());
  return (
    <>
      {bericht.fotos.map((foto) => (
        <Foto key={foto.id} foto={foto} />
      ))}
      {spraak && <Spraak bericht={bericht} />}
      {!tekstIsTranscript && <Inhoud bericht={bericht} />}
      {bericht.bijlagen.length > 0 && (
        <div className="mailbijlagen">
          {bericht.bijlagen.map((b) => (
            <Bijlage key={b.id} bijlage={b} />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Een klantmail zonder `mail` (oudere server): de tekst zonder markeringen,
 * zonder opmaak en zonder herkend citaat.
 */
function mailUitTekst(bericht: V6Bericht): V6Mail {
  return { html: "", extern: false, tekst: zonderMarkering(bericht.tekst), citaat: "" };
}

/** Alleen het mailadres uit "Naam <adres>"; niets als het gelijk is aan de naam. */
function mailAdres(van: string | null, wie: string): string | null {
  if (!van) return null;
  const hoek = /<([^>]+)>/.exec(van);
  const adres = (hoek ? hoek[1] : van).trim();
  return adres && adres !== wie ? adres : null;
}

/**
 * Motor 7 golf 2 (keuze 7): de mail van de klant een op een, met lijntjes:
 * Van (naam en adres) en Onderwerp. Daaronder `MailWeergave` (sessie 186,
 * punt 7): de opgemaakte mail als die er is, anders de tekst zonder
 * markeringen, en het eerdere bericht ingeklapt. Foto's als miniaturen die
 * de lightbox openen.
 */
function KlantMail({ bericht }: { bericht: V6Bericht }) {
  // De server geeft "Naam <adres>" of alleen het adres; de naam staat er al.
  const adres = mailAdres(bericht.van, bericht.wie);
  const spraak = isSpraak(bericht);
  // Zoals in Lijf: is de tekst het transcript, dan niet nog eens.
  const tekstIsTranscript =
    spraak && (!bericht.tekst.trim() || bericht.tekst.trim() === bericht.transcript?.trim());
  return (
    <article className="bericht klantmail" data-testid="klantmail">
      <div className="rij">
        <span className="l">Van</span>
        <span className="van">
          <b>{bericht.wie}</b>
          {adres && <span> {adres}</span>}
        </span>
        <span className="tijd num">{dagKlok(bericht.moment)}</span>
      </div>
      {bericht.onderwerp && (
        <div className="rij">
          <span className="l">Onderwerp</span>
          <span className="ond">{bericht.onderwerp}</span>
          <span />
        </div>
      )}
      {spraak && <Spraak bericht={bericht} />}
      {tekstIsTranscript ? null : (
        <div className="bt lijf">
          <MailWeergave emailId={bericht.id} mail={bericht.mail ?? mailUitTekst(bericht)} />
        </div>
      )}
      {bericht.fotos.length > 0 && (
        <div className="fotos">
          {bericht.fotos.map((foto) => (
            <Foto key={foto.id} foto={foto} />
          ))}
        </div>
      )}
      {bericht.bijlagen.length > 0 && (
        <div className="mailbijlagen">
          {bericht.bijlagen.map((b) => (
            <Bijlage key={b.id} bijlage={b} />
          ))}
        </div>
      )}
    </article>
  );
}

function Mailkaart({ bericht }: { bericht: V6Bericht }) {
  const ons = bericht.kant === "wij";
  const via =
    !ons && bericht.aan
      ? `aan ${bericht.aan}`
      : `via ${KANAAL_WOORD[bericht.kanaal ?? "mail"]}`;
  return (
    <article className={`bericht${ons ? " ons" : ""}`}>
      <div className="bk">
        <span className="av" aria-hidden="true">
          {initiaal(bericht.wie)}
        </span>
        <div>
          <b title={bericht.van ?? undefined}>{bericht.wie}</b> <span>{via}</span>
        </div>
        <span className="tijd">{klokje(bericht.moment)}</span>
      </div>
      <div className="bt">
        {bericht.onderwerp && (
          <p className="ond">
            Onderwerp: <b>{bericht.onderwerp}</b>
          </p>
        )}
        <Lijf bericht={bericht} />
      </div>
    </article>
  );
}

function Ballon({ bericht }: { bericht: V6Bericht }) {
  const ons = bericht.kant === "wij";
  const onder = ons
    ? [
        bericht.wie,
        klokje(bericht.moment),
        bericht.template_naam ? `template ${bericht.template_naam}` : null,
        bericht.verzendstatus,
      ]
        .filter(Boolean)
        .join(", ")
    : klokje(bericht.moment);
  if (bericht.bericht_soort === "knop") {
    // Sessie 75: de klant tikte een knop op WhatsApp; zo staat het er ook.
    return (
      <div className="v6-regel tegel" data-testid="regel-knop">
        <span className="knopje gekozen">{bericht.tekst}</span>
        <div className="m">{onder}</div>
      </div>
    );
  }
  return (
    <div className={`ballon${ons ? " ons" : ""}`}>
      <Lijf bericht={bericht} />
      <div className="m">{onder}</div>
    </div>
  );
}

/** Motor sessie 5b: de uitkomst van de knop in een woord (JP is kleurenblind: nooit alleen kleur). */
const STAND_WOORD: Record<string, string> = {
  uitgevoerd: "Uitgevoerd",
  voorstel_jp: "Niet uitgevoerd, staat bij jou",
  geweigerd: "Niet uitgevoerd",
  proef_niet_uitgevoerd: "Proef, niet uitgevoerd",
  // Motor sessie 8C: het vangnet (de uitvoering faalde); de kaart "Hand niet gelukt" staat op Vandaag.
  mislukt_naar_jp: "Niet gelukt, staat bij jou",
};

function HandKnop({ knop }: { knop: { label: string; pad: string } }) {
  const [bezig, setBezig] = useState(false);
  const [uitkomst, setUitkomst] = useState<string | null>(null);
  const klik = useCallback(async () => {
    setBezig(true);
    try {
      const uit = await v6Api.handUitvoeren(knop.pad);
      const woord = STAND_WOORD[uit.stand] ?? uit.stand;
      setUitkomst(uit.stand === "uitgevoerd" ? woord : `${woord}${uit.reden ? `: ${uit.reden}` : ""}`);
    } catch (fout) {
      // 409: de hand was al gedaan of kan niet meer. Elke andere fout (time-out, server): de
      // uitkomst is niet zeker; nooit "niet uitgevoerd" zeggen als het misschien wel gebeurde.
      const status = (fout as { response?: { status?: number } })?.response?.status;
      setUitkomst(
        status === 409
          ? "Niet uitgevoerd: al gedaan of niet meer mogelijk"
          : "Uitkomst onbekend: kijk of er een kaart Gedaan staat en in Picqer",
      );
    } finally {
      setBezig(false);
    }
  }, [knop.pad]);
  if (uitkomst) {
    return (
      <span className="hand-uitkomst" data-testid="hand-uitkomst">
        {uitkomst}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="v6-knop doe hand-knop"
      data-testid="hand-uitvoeren"
      disabled={bezig}
      onClick={() => void klik()}
    >
      {bezig ? "Bezig" : knop.label}
    </button>
  );
}

function Notitieregel({ bericht }: { bericht: V6Bericht }) {
  return (
    <div className={`noot${bericht.knop ? " met-knop" : ""}`}>
      <Icoon naam="notitie" klein />
      <span>
        <b>{bericht.bericht_soort === "taak" ? "Taak:" : `Intern, ${bericht.wie}:`}</b> {bericht.tekst}
      </span>
      {bericht.knop ? <HandKnop knop={bericht.knop} /> : null}
      <span className="tijd">{klokje(bericht.moment)}</span>
    </div>
  );
}

export function BerichtRegel({
  bericht,
  gekozen = null,
}: {
  bericht: V6Bericht;
  gekozen?: string | null;
}) {
  if (bericht.regel) return <Gespreksregel regel={bericht.regel} gekozen={gekozen} />;
  switch (bericht.soort) {
    case "mail":
      // Keuze 7: de klantmail een op een; voor onze eigen mail blijft de
      // compacte kaart.
      return bericht.kant === "klant" ? (
        <KlantMail bericht={bericht} />
      ) : (
        <Mailkaart bericht={bericht} />
      );
    case "ballon":
      return <Ballon bericht={bericht} />;
    case "notitie":
      return <Notitieregel bericht={bericht} />;
    default:
      return <div className="systeem">{bericht.tekst}</div>;
  }
}

/** Na een kaart met keuzeknoppen: de tekst van het volgende klantbericht (de gekozen knop). */
function volgendeKlanttekst(berichten: V6Bericht[], index: number): string | null {
  if (berichten[index]?.regel?.soort !== "kaart") return null;
  for (const b of berichten.slice(index + 1)) {
    if (b.regel) continue;
    if (b.soort === "ballon" && b.kant === "klant") return b.tekst;
    // Nova's tekst van dezelfde beurt staat na de kaarten; die slaan we over.
    if (b.soort === "mail") return null;
  }
  return null;
}

/** Klant typt, of Nova schrijft net een antwoord: voor de scroll en het scherm. */
export function useLiveRegels(zaak: V6Zaak, nu: number): { typt: boolean; novaBezig: boolean } {
  const live = useLive();
  const typt = live.typt(zaak.gesprek_ids);
  const gesprek = zaak.berichten.filter((b) => !b.regel && (b.soort === "ballon" || b.soort === "mail"));
  const laatste = gesprek[gesprek.length - 1];
  const novaBezig =
    !typt &&
    Boolean(laatste) &&
    laatste.kant === "klant" &&
    nu - new Date(laatste.moment).getTime() < NOVA_BEZIG_MS &&
    live.isLive(zaak.gesprek_ids);
  return { typt, novaBezig };
}

/**
 * Oudere berichten uit dezelfde draad, van vóór deze zaak: één
 * inklapregel, dicht bij openen en bij elke andere zaak. Open staan ze
 * gedempt boven de berichten van nu, met dezelfde weergave.
 */
function Eerdere({ zaak }: { zaak: V6Zaak }) {
  // Een andere zaak begint dicht, ook als JP later terugkomt op deze.
  const [stand, setStand] = useState({ zaakId: zaak.zaak_id, open: false });
  if (stand.zaakId !== zaak.zaak_id) setStand({ zaakId: zaak.zaak_id, open: false });
  const eerdere = zaak.eerdere_berichten ?? [];
  // De teller telt berichten, niet de dagkoppen die de server ertussen zet.
  const aantal = eerdere.filter((b) => b.soort !== "systeem").length;
  if (aantal === 0) return null;
  const open = stand.zaakId === zaak.zaak_id && stand.open;
  return (
    <>
      <button
        type="button"
        className="v6-eerder-knop"
        aria-expanded={open}
        onClick={() => setStand({ zaakId: zaak.zaak_id, open: !open })}
      >
        Eerdere gesprekken ({aantal})
        <Icoon naam="pijl" klein />
      </button>
      {open && (
        <div className="v6-eerder" aria-label="Eerdere gesprekken">
          {eerdere.map((bericht, i) => (
            <BerichtRegel
              key={bericht.id}
              bericht={bericht}
              gekozen={volgendeKlanttekst(eerdere, i)}
            />
          ))}
        </div>
      )}
    </>
  );
}

export function Berichten({
  zaak,
  typt,
  novaBezig,
}: {
  zaak: V6Zaak;
  typt: boolean;
  novaBezig: boolean;
}) {
  return (
    <>
      <Eerdere zaak={zaak} />
      {zaak.berichten.map((bericht, i) => (
        <BerichtRegel
          key={bericht.id}
          bericht={bericht}
          gekozen={volgendeKlanttekst(zaak.berichten, i)}
        />
      ))}
      {typt && (
        <div className="ballon bezig" aria-live="polite">
          typt...
        </div>
      )}
      {novaBezig && (
        <div className="ballon ons bezig" aria-live="polite">
          Nova antwoordt...
        </div>
      )}
    </>
  );
}
